// src/contexts/token/TokenFundamentalsProvider.tsx
/**
 * TokenFundamentalsProvider - Centralizes fundamental valuation data
 * 
 * Fetches and provides:
 * - Historical time series (fees, TVL, DAU, transactions)
 * - Historical ratio series (MC/Fees, MC/TVL, etc.)
 * - Current snapshot ratios
 * 
 * This is a refactored version of TokenValuationContext with cleaner types.
 */

import React, { createContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  fetchTokensHistoryPoints, 
  fetchTokensHistoryRatios,
  fetchTokensCurrentRatios,
  fetchTokensCurrent,
  fetchTokensChanges,
} from "@/services/queries/tokens/tokensScreenerQueries";
import type { TokenFundamentalsContextValue, TimeSeries } from "./types";

// ============================================================================
// CONTEXT
// ============================================================================

const TokenFundamentalsContext = createContext<TokenFundamentalsContextValue | null>(null);

// ============================================================================
// UTILITIES
// ============================================================================

function parseDate(d: string | number): string {
  if (typeof d === 'number') {
    if (d >= 10000101 && d <= 99991231) {
      const y = Math.floor(d / 10000);
      const m = Math.floor((d % 10000) / 100);
      const day = d % 100;
      return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    const ms = d < 1e12 ? d * 1000 : d;
    return new Date(ms).toISOString().slice(0, 10);
  }
  return d.slice(0, 10);
}

function processHistoricalData(
  data: Array<{
    ticker: string;
    metrics?: Array<{
      metric_key: string;
      dates?: (string | number)[];
      values?: (number | null)[];
    }>;
  }>,
  ticker: string,
  metricKey: string
): TimeSeries {
  const empty: TimeSeries = { dates: [], values: [] };
  
  const row = data.find(r => 
    (r.ticker || '').toUpperCase() === ticker.toUpperCase()
  );
  
  if (!row) return empty;
  
  const metric = row.metrics?.find(m => m.metric_key === metricKey);
  if (!metric) return empty;
  
  const rawDates = metric.dates || [];
  const rawValues = metric.values || [];
  
  const dates = rawDates.map(parseDate);
  const values = rawValues.map(v => 
    v === null || v === undefined || !Number.isFinite(Number(v)) 
      ? NaN 
      : Number(v)
  );
  
  return { dates, values };
}

// ============================================================================
// PROVIDER
// ============================================================================

interface TokenFundamentalsProviderProps {
  ticker: string;
  children: React.ReactNode;
}

export const TokenFundamentalsProvider: React.FC<TokenFundamentalsProviderProps> = ({
  ticker,
  children,
}) => {
  const tkr = ticker.toUpperCase();
  
  // Fetch historical time series
  const historyPointsQuery = useQuery({
    queryKey: ['token', 'fundamentals', 'history', 'points', tkr],
    queryFn: async ({ signal }) => 
      fetchTokensHistoryPoints({ 
        tickersCsv: tkr, 
        metricKeysCsv: 'fees,tvl,dau,transactions',
        signal,
      }),
    staleTime: 60_000,
    enabled: !!tkr,
  });
  
  // Fetch historical ratios
  const historyRatiosQuery = useQuery({
    queryKey: ['token', 'fundamentals', 'history', 'ratios', tkr],
    queryFn: async ({ signal }) => 
      fetchTokensHistoryRatios({ 
        tickersCsv: tkr, 
        ratioKeysCsv: 'ratio:marketCapToAnnualisedFees,ratio:marketCapToTvl,ratio:tvlToAnnualisedFees',
        signal,
      }),
    staleTime: 60_000,
    enabled: !!tkr,
  });
  
  // Fetch current snapshot ratios
  const currentRatiosQuery = useQuery({
    queryKey: ['token', 'fundamentals', 'current', 'ratios', tkr],
    queryFn: async ({ signal }) => 
      fetchTokensCurrentRatios({ 
        tickersCsv: tkr, 
        ratioKeysCsv: 'ratio:marketCapToAnnualisedFees,ratio:marketCapToTvl,ratio:marketCapToActiveAdresses,ratio:marketCapToTransactions,ratio:tvlToAnnualisedFees,ratio:tvlToVolume,ratio:annualisedFeesToTransactions,ratio:annualisedFeesToActiveAdresses',
        signal,
      }),
    staleTime: 60_000,
    enabled: !!tkr,
  });
  
  // Fetch current snapshot values (market cap, volume, tvl, fees, revenue)
  const snapshotQuery = useQuery({
    queryKey: ['token', 'fundamentals', 'snapshot', tkr],
    queryFn: async ({ signal }) => 
      fetchTokensCurrent({ 
        tickersCsv: tkr, 
        metricKeysCsv: 'market_cap,volume,tvl,fees,revenue',
        signal,
      }),
    staleTime: 60_000,
    enabled: !!tkr,
  });
  
  // Fetch price changes by window (fallback for performance displays)
  const priceChangesQuery = useQuery({
    queryKey: ['token', 'fundamentals', 'priceChanges', tkr],
    queryFn: async ({ signal }) => 
      fetchTokensChanges({ 
        tickersCsv: tkr, 
        metricKeysCsv: 'price_close',
        windowsCsv: '1d,7d,30d,60d,90d,180d,365d',
        signal,
      }),
    staleTime: 60_000,
    enabled: !!tkr,
  });
  
  const value = useMemo<TokenFundamentalsContextValue>(() => {
    const pointsData = historyPointsQuery.data || [];
    const ratiosData = historyRatiosQuery.data || [];
    const currentData = currentRatiosQuery.data?.tokens?.[0]?.metrics || {};
    const snapshotData = snapshotQuery.data?.tokens?.[0]?.metrics || {};
    
    // Process historical time series
    const fees = processHistoricalData(pointsData, tkr, 'fees');
    const tvl = processHistoricalData(pointsData, tkr, 'tvl');
    const dau = processHistoricalData(pointsData, tkr, 'dau');
    const transactions = processHistoricalData(pointsData, tkr, 'transactions');
    
    // Process historical ratios
    const mcToFeesHistory = processHistoricalData(ratiosData, tkr, 'ratio:marketCapToAnnualisedFees');
    const mcToTvlHistory = processHistoricalData(ratiosData, tkr, 'ratio:marketCapToTvl');
    
    // Current snapshot values
    const snapshot = {
      marketCap: snapshotData['market_cap'] ?? null,
      volume: snapshotData['volume'] ?? null,
      tvl: snapshotData['tvl'] ?? null,
      fees: snapshotData['fees'] ?? null,
      revenue: snapshotData['revenue'] ?? null,
    };
    
    // Process price changes by window
    const priceChangesToken = priceChangesQuery.data?.tokens?.[0];
    const priceMetric = priceChangesToken?.metrics?.find(
      (m: { metric_key: string }) => m.metric_key === 'price_close'
    );
    const priceWindows = priceMetric?.windows || {};
    
    // Helper to extract change value (API returns decimal, we store as percent)
    const getChange = (window: string): number | null => {
      const w = priceWindows[window] as { value?: number | null } | undefined;
      const val = w?.value;
      return val != null ? val * 100 : null; // Convert to percent
    };
    
    const priceChanges = {
      '1d': getChange('1d'),
      '7d': getChange('7d'),
      '30d': getChange('30d'),
      '60d': getChange('60d'),
      '90d': getChange('90d'),
      '180d': getChange('180d'),
      '365d': getChange('365d'),
    };
    
    // Current ratios
    const current = {
      mcToFees: currentData['ratio:marketCapToAnnualisedFees'] ?? null,
      mcToTvl: currentData['ratio:marketCapToTvl'] ?? null,
      mcToDau: currentData['ratio:marketCapToActiveAdresses'] ?? null,
      mcToTx: currentData['ratio:marketCapToTransactions'] ?? null,
      tvlToFees: currentData['ratio:tvlToAnnualisedFees'] ?? null,
      tvlToVol: currentData['ratio:tvlToVolume'] ?? null,
      feesToTx: currentData['ratio:annualisedFeesToTransactions'] ?? null,
      feesToDau: currentData['ratio:annualisedFeesToActiveAdresses'] ?? null,
    };
    
    const isLoading = historyPointsQuery.isLoading || historyRatiosQuery.isLoading || 
                      currentRatiosQuery.isLoading || snapshotQuery.isLoading || priceChangesQuery.isLoading;
    const isError = historyPointsQuery.isError || historyRatiosQuery.isError || 
                    currentRatiosQuery.isError || snapshotQuery.isError || priceChangesQuery.isError;
    const error = historyPointsQuery.error || historyRatiosQuery.error || 
                  currentRatiosQuery.error || snapshotQuery.error || priceChangesQuery.error;
    
    return {
      ticker: tkr,
      fees,
      tvl,
      dau,
      transactions,
      mcToFeesHistory,
      mcToTvlHistory,
      snapshot,
      priceChanges,
      current,
      isLoading,
      isError,
      error: error as Error | null,
      refetch: () => {
        historyPointsQuery.refetch();
        historyRatiosQuery.refetch();
        currentRatiosQuery.refetch();
        snapshotQuery.refetch();
        priceChangesQuery.refetch();
      },
    };
  }, [
    historyPointsQuery,
    historyRatiosQuery,
    currentRatiosQuery,
    snapshotQuery,
    priceChangesQuery,
    tkr,
  ]);
  
  return (
    <TokenFundamentalsContext.Provider value={value}>
      {children}
    </TokenFundamentalsContext.Provider>
  );
};

export { TokenFundamentalsContext };

